import React from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"

import {
  canEdit,
  getFieldValues,
  getSectionFieldNames,
  getSettings,
  hasProductField,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import FormikBootstrap from "@/components/formik-bootstrap"
import { ReferencedValueTypes, ReservedFields } from "@/onboarding/features/product/models/Product"
import LoadingSpinner from "@/components/LoadingSpinner"
import { useProductSubmitting } from "@/onboarding/features/product/helpers"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"

const Edit = ({ setEditing }): JSX.Element => {
  const fieldNames = useSelector(getSectionFieldNames.tourLocation)
  const initialValues = useSelector(getFieldValues(...fieldNames))
  const fieldsSettings = useSelector(getSettings(...fieldNames))
  const canEditProduct = useSelector(canEdit)
  const hasMeetingLocation = useSelector(hasProductField(ReservedFields.MEETING_LOCATION))
  const hasAnchoredBuffer = useSelector(
    hasProductField(ReservedFields.MEETING_TIME_BUFFER_ANCHORED)
  )
  const hasDockedBuffer = useSelector(hasProductField(ReservedFields.MEETING_TIME_BUFFER_DOCKED))

  const dispatch = useDispatch()
  const [isSubmitting, setSubmitting] = useProductSubmitting()

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(updateFields({ values }))
      }}
    >
      {({ handleSubmit, dirty }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <TrackFormDirty formId="tourItineraryOverview" />
          <Row>
            <Col md={4}>
              <FormikBootstrap.TextInput
                name={ReservedFields.START_LOCATION}
                label="Start Location"
                readOnly={!canEditProduct}
                showLockedIcon={!canEditProduct}
                showRequired={fieldsSettings[ReservedFields.START_LOCATION].required}
              />
            </Col>
            {hasMeetingLocation && (
              <Col md={4}>
                <ReferencedValueSelect
                  name={ReservedFields.MEETING_LOCATION}
                  type={ReferencedValueTypes.MEETING_LOCATION}
                  label="Meeting Location"
                />
              </Col>
            )}
            <Col md={4}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TOUR_DURATION}
                label="Tour Duration (minutes)"
                readOnly={!canEditProduct}
                showLockedIcon={!canEditProduct}
                showRequired={fieldsSettings[ReservedFields.TOUR_DURATION].required}
              />
            </Col>
          </Row>
          {(hasAnchoredBuffer || hasDockedBuffer) && (
            <Row>
              {hasAnchoredBuffer && (
                <Col md={4}>
                  <FormikBootstrap.NumberInput
                    name={ReservedFields.MEETING_TIME_BUFFER_ANCHORED}
                    label="Meeting Time - Anchored"
                    helpText="Minutes before departure"
                  />
                </Col>
              )}
              {hasDockedBuffer && (
                <Col md={4}>
                  <FormikBootstrap.NumberInput
                    name={ReservedFields.MEETING_TIME_BUFFER_DOCKED}
                    label="Meeting Time - Docked"
                    helpText="Minutes before departure"
                  />
                </Col>
              )}
            </Row>
          )}
          <div className="d-flex gap-2">
            <Button size="sm" type="submit" disabled={!dirty || isSubmitting}>
              <i className="fas fa-save me-2" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setEditing(false)}
              disabled={isSubmitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            {isSubmitting && <LoadingSpinner />}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
